import { t } from 'i18next';
import { Icons } from '@Components/icon/Icon';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { useBoundStore } from '@Store/useBoundStore';
import { ErrorModalTypes, OnboardingFormFields } from '@Types/index';
import { GenericMessageTypes } from '@Pages/genericMessagePage/queryParamsSchema';
import { reportViewExpireToken } from '@Services/analytics/GeneralAnalaytics';
import { redirectToWebAppLoginPage } from '../../utils';
import { ErrorType } from './errorType';
import type { IErrorHandling } from '@Types/index';

export const AuthErrorMap = {
  TOKEN_EXPIRED: { errorCode: 401, errorType: ErrorType.BUSINESS_LOGIC },
  ACCESS_DENIED: { errorCode: 40001, errorType: ErrorType.TECHNICAL },
  ACCESS_DENIED_IP_NOT_ALLOWED: { errorCode: 40002, errorType: ErrorType.TECHNICAL },
  CUSTOMER_NOT_FOUND: { errorCode: 4003, errorType: ErrorType.BUSINESS_LOGIC },
  INCORRECT_USER_PASSWORD: { errorCode: 40033, errorType: ErrorType.BUSINESS_LOGIC },
  OKTA_USER_NOT_EXISTS: { errorCode: 40034, errorType: ErrorType.TECHNICAL },
  INCORRECT_USER_PASSWORD_BAD_REQUEST: { errorCode: 40035, errorType: ErrorType.BUSINESS_LOGIC },
  MFA_FACTOR_ALREADY_EXISTS: { errorCode: 40030, errorType: ErrorType.TECHNICAL },
  MFA_ALREADY_ACTIVATE: { errorCode: 40036, errorType: ErrorType.TECHNICAL },
  MFA_NOT_ACTIVE: { errorCode: 40037, errorType: ErrorType.TECHNICAL },
  MFA_NOT_PENDING: { errorCode: 40038, errorType: ErrorType.TECHNICAL },
  MFA_REQUIRE: { errorCode: 4039, errorType: ErrorType.BUSINESS_LOGIC },
  MFA_BAD_STATE_TOKEN: { errorCode: 40040, errorType: ErrorType.TECHNICAL },
  MFA_NOT_EXISTS: { errorCode: 40041, errorType: ErrorType.TECHNICAL },
  MFA_BAD_STATE_TOKEN_EXPIRE: { errorCode: 40042, errorType: ErrorType.TECHNICAL },
  MFA_CODE_IS_INVALID: { errorCode: 40032, errorType: ErrorType.BUSINESS_LOGIC },
  JWT_TOKEN_EXPIRE: { errorCode: 40043, errorType: ErrorType.BUSINESS_LOGIC },
  JWT_BAD_TOKEN: { errorCode: 40044, errorType: ErrorType.TECHNICAL },
  MFA_SECURITY_ANSWER_WEAK: { errorCode: 40045, errorType: ErrorType.TECHNICAL },
  MFA_INVALID_QUESTION: { errorCode: 40046, errorType: ErrorType.TECHNICAL },
  REFRESH_TOKEN_EXPIRE: { errorCode: 40047, errorType: ErrorType.TECHNICAL },
  BAD_CLIENT_ID: { errorCode: 40048, errorType: ErrorType.TECHNICAL },
  INCORRECT_USER_PASSWORD_FOR_BROWSER: { errorCode: 40049, errorType: ErrorType.BUSINESS_LOGIC },
  MFA_BAD_TYPE: { errorCode: 40050, errorType: ErrorType.TECHNICAL },
  BAD_TOKEN: { errorCode: 40052, errorType: ErrorType.TECHNICAL },
  USER_LOCKED: { errorCode: 4026, errorType: ErrorType.TECHNICAL },
  CAPTCHA_FAILED: { errorCode: 4056, errorType: ErrorType.BUSINESS_LOGIC },
  CUSTOMER_LEAD_NOT_FOUND: { errorCode: 4057, errorType: ErrorType.TECHNICAL },
  AUTHORIZED_USER_NOT_EXIST: { errorCode: 50705, errorType: ErrorType.BUSINESS_LOGIC },
  AUTHORIZED_USER_INVITE_CANCELED: { errorCode: 50722, errorType: ErrorType.BUSINESS_LOGIC },
  AUTHORIZED_USER_INVITE_EXPIRED: { errorCode: 50723, errorType: ErrorType.BUSINESS_LOGIC },
  AUTHORIZED_USER_ALREADY_REGISTER: { errorCode: 50724, errorType: ErrorType.BUSINESS_LOGIC },
  AUTHORIZED_USER_ALREADY_COMPLETE_ONBOARDING: { errorCode: 50725, errorType: ErrorType.TECHNICAL },
  MFA_CODE_ALREADY_USED: { errorCode: 40082, errorType: ErrorType.TECHNICAL },
  MFA_TOO_MANY_RESEND_REQUEST: { errorCode: 40031, errorType: ErrorType.TECHNICAL },
  TOKEN_NOT_MATCH_OUR_RECORDS: { errorCode: 4079, errorType: ErrorType.TECHNICAL },
  UBO_INVITE_NOT_EXISTS: { errorCode: 5036, errorType: ErrorType.BUSINESS_LOGIC },
  UBO_INVITE_CANCELED: { errorCode: 5037, errorType: ErrorType.BUSINESS_LOGIC },
  UBO_INVITE_EXPIRED: { errorCode: 5035, errorType: ErrorType.BUSINESS_LOGIC },
  CUSTOMER_NOT_PARTNER_FULL_ACCESS_ROLE: { errorCode: 5034, errorType: ErrorType.BUSINESS_LOGIC },
  UBO_ALREADY_COMPLETE_ONBOARDING: { errorCode: 50735, errorType: ErrorType.BUSINESS_LOGIC },
  ACCOUNTANT_INVITATION_ALREADY_COMPLETED: {
    errorCode: 42003,
    errorType: ErrorType.BUSINESS_LOGIC,
  },
  AUTHORIZED_USER_KYC_FAILED: { errorCode: 42051, errorType: ErrorType.BUSINESS_LOGIC },
} as const;

const authErrorModalHandler = () => {
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();
  if (isEmbedded) {
    const httpOrHttps = window.location.protocol;
    const hostname =
      window.location.hostname == 'localhost' ? 'localhost:8082' : window.location.hostname;
    const token = useBoundStore.getState().onboardingSlice.partnerToken;
    const url = `${httpOrHttps}//${hostname}/?token=${token}`;
    window.location.replace(url);
  }
};

export const getAuthErrorsObjectMap = (): Map<number, IErrorHandling> => {
  return new Map<number, IErrorHandling>([
    [
      AuthErrorMap.CAPTCHA_FAILED.errorCode,
      {
        errorCode: AuthErrorMap.USER_LOCKED.errorCode,
        redirectPath: routesModel[RoutesMap.WELCOME].path,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.CUSTOMER_LEAD_NOT_FOUND.errorCode,
      {
        errorCode: AuthErrorMap.USER_LOCKED.errorCode,
        redirectPath: routesModel[RoutesMap.WELCOME].path,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.USER_LOCKED.errorCode,
      {
        errorCode: AuthErrorMap.USER_LOCKED.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.USER_LOCKED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {
          useBoundStore.getState().onboardingSlice.setIsOnboardingLocked(true);
        },
      },
    ],
    [
      AuthErrorMap.TOKEN_EXPIRED.errorCode,
      {
        errorCode: AuthErrorMap.TOKEN_EXPIRED.errorCode,
        redirectPath: routesModel[RoutesMap.WELCOME].path,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {
          // Clear access tokens when token expires
          const setCustomerAccessToken = useBoundStore.getState().setCustomerAccessToken;
          const setAuthUserAccessToken = useBoundStore.getState().setAuthUserAccessToken;
          setCustomerAccessToken('');
          setAuthUserAccessToken('');
          useBoundStore.getState().setOnboardingAuthenticated(false);

          const routerData = useBoundStore.getState().onboardingSlice.routerData;
          reportViewExpireToken(routerData.currentRoute);
        },
        openModal: true,
        modalProps: {
          title: t('enErrors:auth.title'),
          content: t('enErrors:auth.description'),
          icon: Icons.Oops,
          buttonText: t('enErrors:auth.button'),
          type: ErrorModalTypes.GENERAL,
          onSubmit: () => {
            authErrorModalHandler();
          },
        },
      },
    ],
    [
      AuthErrorMap.CUSTOMER_NOT_FOUND.errorCode,
      {
        errorCode: AuthErrorMap.CUSTOMER_NOT_FOUND.errorCode,
      },
    ],
    [
      AuthErrorMap.INCORRECT_USER_PASSWORD.errorCode,
      {
        errorCode: AuthErrorMap.INCORRECT_USER_PASSWORD.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.PASSWORD,
        callBack: () => {},
      },
    ],

    [
      AuthErrorMap.INCORRECT_USER_PASSWORD_BAD_REQUEST.errorCode,
      {
        errorCode: AuthErrorMap.INCORRECT_USER_PASSWORD_BAD_REQUEST.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.PASSWORD,
        callBack: () => {},
      },
    ],

    [
      AuthErrorMap.JWT_TOKEN_EXPIRE.errorCode,
      {
        errorCode: AuthErrorMap.JWT_TOKEN_EXPIRE.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
        openModal: true,
        modalProps: {
          title: t('enErrors:auth.title'),
          content: t('enErrors:auth.description'),
          icon: Icons.Oops,
          buttonText: t('enErrors:auth.button'),
          type: ErrorModalTypes.GENERAL,
          onSubmit: () => {
            authErrorModalHandler();
          },
        },
      },
    ],
    [
      AuthErrorMap.JWT_BAD_TOKEN.errorCode,
      {
        errorCode: AuthErrorMap.JWT_BAD_TOKEN.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
        openModal: true,
        modalProps: {
          title: t('enErrors:jwt.title'),
          content: t('enErrors:jwt.description'),
          icon: Icons.Oops,
          buttonText: t('enErrors:jwt.button'),
          type: ErrorModalTypes.GENERAL,
          onSubmit: () => {
            authErrorModalHandler();
          },
        },
      },
    ],

    [
      AuthErrorMap.INCORRECT_USER_PASSWORD_FOR_BROWSER.errorCode,
      {
        errorCode: AuthErrorMap.INCORRECT_USER_PASSWORD_FOR_BROWSER.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.PASSWORD,
        callBack: () => {},
      },
    ],

    [
      AuthErrorMap.BAD_TOKEN.errorCode,
      {
        errorCode: AuthErrorMap.BAD_TOKEN.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
        openModal: true,
        modalProps: {
          title: t('enErrors:jwt.title'),
          content: t('enErrors:jwt.description'),
          icon: Icons.Oops,
          buttonText: t('enErrors:jwt.button'),
          type: ErrorModalTypes.GENERAL,
          onSubmit: () => {
            authErrorModalHandler();
          },
        },
      },
    ],
    [
      AuthErrorMap.AUTHORIZED_USER_NOT_EXIST.errorCode,
      {
        errorCode: AuthErrorMap.AUTHORIZED_USER_NOT_EXIST.errorCode,
      },
    ],
    [
      AuthErrorMap.AUTHORIZED_USER_INVITE_CANCELED.errorCode,
      {
        errorCode: AuthErrorMap.AUTHORIZED_USER_INVITE_CANCELED.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.AUTHORIZED_USER_INVITE_EXPIRED.errorCode,
      {
        errorCode: AuthErrorMap.AUTHORIZED_USER_INVITE_EXPIRED.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.AUTHORIZED_USER_ALREADY_REGISTER.errorCode,
      {
        errorCode: AuthErrorMap.AUTHORIZED_USER_ALREADY_REGISTER.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.AUTHORIZED_USER_ALREADY_COMPLETE_ONBOARDING.errorCode,
      {
        errorCode: AuthErrorMap.AUTHORIZED_USER_ALREADY_COMPLETE_ONBOARDING.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.MFA_CODE_ALREADY_USED.errorCode,
      {
        errorCode: AuthErrorMap.MFA_CODE_ALREADY_USED.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.MFA_OTP,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.MFA_BAD_STATE_TOKEN.errorCode,
      {
        errorCode: AuthErrorMap.MFA_BAD_STATE_TOKEN.errorCode,
        redirectPath: routesModel[RoutesMap.WELCOME].path,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
        openModal: true,
        modalProps: {
          title: t('enErrors:auth.title'),
          content: t('enErrors:auth.description'),
          icon: Icons.Oops,
          buttonText: t('enErrors:auth.button'),
          type: ErrorModalTypes.GENERAL,
          onSubmit: () => {
            authErrorModalHandler();
          },
        },
      },
    ],
    [
      AuthErrorMap.MFA_NOT_EXISTS.errorCode,
      {
        errorCode: AuthErrorMap.MFA_NOT_EXISTS.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.MFA_OTP,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.MFA_BAD_STATE_TOKEN_EXPIRE.errorCode,
      {
        errorCode: AuthErrorMap.MFA_BAD_STATE_TOKEN_EXPIRE.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.MFA_OTP,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.MFA_CODE_IS_INVALID.errorCode,
      {
        errorCode: AuthErrorMap.MFA_CODE_IS_INVALID.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.MFA_OTP,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.MFA_TOO_MANY_RESEND_REQUEST.errorCode,
      {
        errorCode: AuthErrorMap.MFA_TOO_MANY_RESEND_REQUEST.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.TOKEN_NOT_MATCH_OUR_RECORDS.errorCode,
      {
        errorCode: AuthErrorMap.TOKEN_NOT_MATCH_OUR_RECORDS.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.UBO_INVITE_NOT_EXISTS.errorCode,
      {
        errorCode: AuthErrorMap.UBO_INVITE_NOT_EXISTS.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.UBO_INVITE_CANCELED.errorCode,
      {
        errorCode: AuthErrorMap.UBO_INVITE_CANCELED.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.UBO_INVITE_EXPIRED.errorCode,
      {
        errorCode: AuthErrorMap.UBO_INVITE_EXPIRED.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.AUTHORIZED_USER_INVITE_CANCELED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.UBO_ALREADY_COMPLETE_ONBOARDING.errorCode,
      {
        errorCode: AuthErrorMap.UBO_ALREADY_COMPLETE_ONBOARDING.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.UBO_ALREADY_COMPLETE_ONBOARDING}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.CUSTOMER_NOT_PARTNER_FULL_ACCESS_ROLE.errorCode,
      {
        errorCode: AuthErrorMap.CUSTOMER_NOT_PARTNER_FULL_ACCESS_ROLE.errorCode,
        redirectPath: `${routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path}?type=${GenericMessageTypes.UBO_INVITE_LINK_EXPIRED}`,
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {},
      },
    ],
    [
      AuthErrorMap.ACCOUNTANT_INVITATION_ALREADY_COMPLETED.errorCode,
      {
        errorCode: AuthErrorMap.ACCOUNTANT_INVITATION_ALREADY_COMPLETED.errorCode,
        redirectPath: '',
        relatedFieldName: OnboardingFormFields.GENERIC_ERROR_FIELD,
        callBack: () => {
          redirectToWebAppLoginPage();
        },
      },
    ],
    [
      // The backend can reject an authorized user's KYC after they submit their personal
      // details. Treat it like the other terminal authorized-user failure (DocV rejected)
      // by sending them to the verification failed screen instead of "unknown error".
      AuthErrorMap.AUTHORIZED_USER_KYC_FAILED.errorCode,
      {
        errorCode: AuthErrorMap.AUTHORIZED_USER_KYC_FAILED.errorCode,
        redirectPath: routesModel[RoutesMap.VERIFICATION_FAILED].path,
        callBack: () => {},
      },
    ],
  ]);
};
